import React from 'react';

import ReactDOM from 'react-dom';

import { frontend } from 'pageflow-scrolled/frontend';

import styles from './footerWithChat.module.css';

import { Footer } from "../footer";

import pageflowLogo from './images/pageflowLogo.jpeg';
import userAvatar from './images/userAvatar.png';
import speechBubbleIcon from '../../images/icons/speechBubble.svg';

// NLUX chat is imported lazily on the client to avoid SSR issues
const AiChatLazy = React.lazy(() =>
  Promise.all([
    import('@nlux/react'),
    import('./pageflow-theme.css')
  ]).then(([m]) => ({default: m.AiChat}))
);

let aiChatSessionId;

frontend.widgetTypes.register('websiteFooterWithChat', {
  component: FooterWithChat
});

function FooterWithChat({configuration}) {
  return (
    <>
      <AiChatWidget
        configuration={configuration}
      />
      <Footer/>
    </>
  )
}

function AiChatWidget({configuration}) {
  const [chatWindowOpen, setChatWindowOpen] = React.useState(false);

  return (
    <>
      {!chatWindowOpen &&
        <button
          className={styles.chatButton}
          aria-label="Open AI Chat"
          onClick={() => setChatWindowOpen(true)}
        >
          <img src={speechBubbleIcon} alt="" width="32" height="32" />
        </button>
      }

      {chatWindowOpen &&
        <ChatWindow
          configuration={configuration}
          onCloseButtonClick={() => setChatWindowOpen(false)}
        />
      }
    </>
  );
}

function QuickRepliesInline({quickReplies, onPick}) {
  if (!quickReplies || quickReplies.length === 0) return null;
  return (
    <div className={styles.quickRepliesInline} role="list" aria-label="Vorgeschlagene Antworten">
      {quickReplies.map((item, index) => (
        <button
          key={`quickReply-${index}`}
          className={styles.quickReplyButton}
          type="button"
          onClick={() => onPick(item)}
          role="listitem"
          aria-label={`Vorschlag: ${item.title}`}
        >
          {item.title}
        </button>
      ))}
    </div>
  );
}

function ChatWindow({configuration, onCloseButtonClick}) {
  const {backendUrl, assistantAvatarUrl, assistantName, welcomeMessage} = configuration;
  const generateSessionId = () => {
    if (window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }
  };

  const getSessionId = () => {
    const key = 'aiChatSessionId';
    try {
      const storage = window.sessionStorage;
      let id = storage.getItem(key);
      if (!id) {
        id = generateSessionId();
        try { storage.setItem(key, id); } catch (e) { /* ignore quota/errors */ }
      }
      return id;
    } catch (e) {
      // Storage may be unavailable/blocked; fallback to in-memory value for the page lifetime (module-scoped)
      if (!aiChatSessionId) aiChatSessionId = generateSessionId();
      return aiChatSessionId;
    }
  };

  const [sessionId] = React.useState(() => getSessionId())

  const chatRootRef = React.useRef(null);
  const [conversationContainer, setConversationContainer] = React.useState(null);
  const hostRef = React.useRef(null);

  const conversationOptions = {
    layout: 'bubbles',
    autoScroll: true,
  }

  const extractQuickReplies = (obj) => {
    if (!obj) return [];
    const src = Array.isArray(obj) ? obj[0] : obj;
    const list = src.quickReplies || [];
    return Array.isArray(list) ? list : [];
  }

  const assistantPersona = {
    name: assistantName,
    avatar: assistantAvatarUrl || pageflowLogo,
    tagline: welcomeMessage,
  };

  const userPersona = {
    avatar: userAvatar,
  };

  const personaOptions = {
    assistant: assistantPersona,
    user: userPersona
  };

  const [quickReplies, setQuickReplies] = React.useState([]);
  const lastQuickRepliesRef = React.useRef([]);
  React.useEffect(() => { lastQuickRepliesRef.current = quickReplies; }, [quickReplies]);

  const events = {
    ready: () => {
      window.plausible?.('AI Chat: Open');
    },
    messageSent: () => {
      window.plausible?.('AI Chat: Message Sent');
    }
  };

  const displayOptions = {
    themeId: 'pageflow',
    colorScheme: 'light',
    width: '100%',
    height: '100%'
  }

  // Track NLUX conversation container and insert a stable host just above the composer
  React.useEffect(() => {
    const root = chatRootRef.current;
    if (!root) return;

    const queryConversation = () => root.querySelector('.nlux-conversation-container')
      || root.querySelector('.nlux-comp-conversation');

    let cancelled = false;

    const ensureContainer = () => {
      if (cancelled) return;
      const c = queryConversation();
      if (c && c !== conversationContainer) {
        setConversationContainer(c);
      }
    };

    const observer = new MutationObserver(() => {
      if (!conversationContainer || !conversationContainer.isConnected) {
        ensureContainer();
      }
    });
    observer.observe(root, {childList: true, subtree: true});

    ensureContainer();
    const intervalId = setInterval(() => {
      if (!conversationContainer || !conversationContainer.isConnected) {
        ensureContainer();
      }
    }, 750);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      observer.disconnect();
    };
  }, [conversationContainer]);

  // Maintain a host element inside the conversation container, positioned just above the composer
  React.useEffect(() => {
    const container = conversationContainer;
    if (!container) return;

    const ensureHost = () => {
      // Create host if needed
      if (!hostRef.current || !hostRef.current.isConnected) {
        hostRef.current = document.createElement('div');
        // Optional class for future styling hooks (no absolute positioning)
        hostRef.current.className = '';
      }
      // Insert host just before the composer inside the conversation container
      const composer = container.querySelector('.nlux-comp-composer');
      if (composer) {
        if (hostRef.current.parentNode !== container || hostRef.current.nextSibling !== composer) {
          container.insertBefore(hostRef.current, composer);
        }
      } else if (hostRef.current.parentNode !== container) {
        container.appendChild(hostRef.current);
      }
    };

    ensureHost();

    const observer = new MutationObserver(() => {
      ensureHost();
    });
    observer.observe(container, {childList: true, subtree: true});

    return () => {
      observer.disconnect();
    };
  }, [conversationContainer]);

  const adapter = {
    batchText: async (message) => {
      // Hide current quick replies as soon as the user sends a message
      const prev = lastQuickRepliesRef.current;
      setQuickReplies([]);

      try {
        const response = await fetch(backendUrl, {
          method: 'POST', headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
          body: JSON.stringify({sessionId, chatInput: message}), mode: 'cors'
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const responseJson = await response.json();
        const replies = extractQuickReplies(responseJson);
        setQuickReplies(replies);
        return (typeof responseJson.output === 'string') ? responseJson.output : JSON.stringify(responseJson);
      } catch (e) {
        // Restore previous quick replies so the UI doesn't get stuck empty on error
        setQuickReplies(prev);
        throw e;
      }
    }
  }

  // Quick Reply als Benutzereingabe an den Adapter schicken
  const submitToComposer = (quickReply) => {
    const composer = document.getElementsByClassName("nlux-comp-composer")?.[0];
    if (!composer) return;

    const textarea = composer.querySelector('textarea');
    const submitBtn = composer.querySelector('button');

    // Use the native setter, not React's overridden one:
    const nativeSetter = Object.getOwnPropertyDescriptor(
      window.HTMLTextAreaElement.prototype,
      'value'
    )?.set;

    textarea.focus();
    nativeSetter.call(textarea, quickReply.payload);

    // Dispatch an actual input event so React/NLUX updates its state
    const inputEvent = new Event('input', {bubbles: true});
    textarea.dispatchEvent(inputEvent);

    // Slight delay to ensure state is updated before clicking submit
    setTimeout(() => {
      submitBtn.click();
    }, 100);
  }

  return (
    <div className={styles.chatWindow}>
      <div
        ref={chatRootRef}
        className={styles.chatContent}
      >
        <button
          className={styles.closeButton}
          aria-label="Chat schließen"
          onClick={onCloseButtonClick}
        >
          ✕
        </button>
        <React.Suspense fallback={null}>
          <AiChatLazy
            adapter={adapter}
            events={events}
            personaOptions={personaOptions}
            conversationOptions={conversationOptions}
            displayOptions={displayOptions}
          />
        </React.Suspense>
        {quickReplies.length > 0 && hostRef.current?.isConnected && (
          ReactDOM.createPortal(
            <QuickRepliesInline quickReplies={quickReplies} onPick={submitToComposer}/> ,
            hostRef.current
          )
        )}
        <div className={styles.betaBanner}>
          Der KI-Assistent befindet sich noch in der Testphase und kann
          Fehler machen. <a href="https://pageflow.freshdesk.com/de/support/tickets/new">Feedback geben</a>
        </div>
      </div>
    </div>
  )
}
